// Stage 3 — Quality Check (FR-3.*, July-2 FR-J13–J16, July-7 FR-K12–K16). The queue is grouped
// by preprint under a COLLAPSIBLE title header (FR-K13); each reviewer card carries the full
// detail set — email, PubMed, nearest neighbor, institutional profile, justification, proposing
// student name + email (FR-K12). Fixable/Reject prompt for STUDENT-facing feedback and Double
// Check for a SENIOR-AE-facing internal note (FR-K14). Students without QC access get "My
// Quality Checks" — their proposals + AE feedback with Edit, Reply, and Fixable → Fixed (FR-K16).
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DOMAINS,
  REVIEWER_STATUSES,
  RRID_TEAMS,
  nextStates,
  type Action,
  type Domain,
  type PreprintStatus,
  type ProposalView,
  type ReviewerStatus,
} from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, CardBody, EmptyState, Field, Input, Modal, Select, Spinner, statusTone } from "../../components/ui.js";
import { MultiFilter } from "../../components/MultiFilter.js";
import { ReviewerProfilePanel } from "../preprints/ReviewerProfilePanel.js";

/** QC rows arrive enriched with the preprint title + selectedId for grouping (FR-J15/J16) and
 *  the pipeline status backing the in-process/complete queue split (Frida-QC R-003/R-004).
 *  preprintAbstract backs the student My-QC expand-in-place disclosure. */
type QcRow = ProposalView & {
  preprintTitle: string;
  selectedId: string | null;
  preprintStatus: PreprintStatus | null;
  preprintAbstract: string | null;
};

/** Linked preprint title + optional abstract disclosure (My Quality Checks + AE QC queue).
 *  Links to `/preprints/:selectedId` (pitch deck detail). */
function PreprintContext({
  selectedId,
  title,
  abstract,
  trailing,
  compact = false,
}: {
  selectedId: string | null | undefined;
  title: string;
  abstract: string | null | undefined;
  trailing?: ReactNode;
  /** Smaller title styling when shown under a reviewer name on Other-feedback cards. */
  compact?: boolean;
}): JSX.Element {
  const heading = selectedId ? (
    <Link
      to={`/preprints/${selectedId}`}
      className="text-brand hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {title}
    </Link>
  ) : (
    <span>{title}</span>
  );
  const body = abstract?.trim();
  return (
    <div className="min-w-0">
      <div className={compact ? "text-xs text-slate-500" : "text-sm font-semibold text-slate-700"}>
        {!compact && "📄 "}
        {heading}
        {trailing}
      </div>
      {body ? (
        <details className="mt-1" onClick={(e) => e.stopPropagation()}>
          <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700">
            Abstract
          </summary>
          <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">{body}</p>
        </details>
      ) : null}
    </div>
  );
}

// Trimmed dropdown (FR-J13): Tentative Invite deduped into Double Check; H-index gates,
// Incomplete, and the old Invite Backup are retired.
const STATUS_ACTIONS: Partial<Record<ReviewerStatus, Action>> = {
  Invite: "qcSetInvite",
  "Invite Backup Unchecked": "qcSetInviteBackup",
  "Double Check": "qcSetDoubleCheck",
  Reject: "qcSetReject",
  Fixable: "qcSetFixable",
  Unchecked: "qcSetUnchecked",
  Fixed: "markReviewerFixed",
  "Student Reject": "studentRejectCandidate",
};
const SETTABLE_STATUSES = Object.keys(STATUS_ACTIONS) as ReviewerStatus[];

// Comment popups per status (FR-K14): who reads the comment decides where it's stored.
// Frida-QC R-012: the prompts coach a positive + corrective tone, and Invite takes an OPTIONAL
// celebratory note (Fixable/Reject/Double Check stay required).
const COMMENT_PROMPTS: Partial<Record<ReviewerStatus, string>> = {
  Fixable: "Feedback for the proposing student (required for Fixable) — start with what they got right, then the fix:",
  Reject: "Feedback for the proposing student (required for Reject) — note what was good before the reason:",
  "Double Check": "Internal note for the senior AE (required for Double Check):",
  Invite: "Optional note for the proposing student — what made this reviewer a great pick? (Leave empty to skip.)",
};
const OPTIONAL_COMMENT_STATUSES = new Set<ReviewerStatus>(["Invite"]);

/** Older imported comments carry literal `\n` sequences — render them as real line breaks. */
const unescapeNewlines = (s: string): string => s.replace(/\\n/g, "\n");

/** Best-match link target (R-010): a full URL as-is; a bare DOI resolves via doi.org. */
const bestMatchHref = (v: string): string => (/^https?:\/\//i.test(v) ? v : `https://doi.org/${v}`);

/** Frida-QC R-011: PubMed research links open most-recent-first (Frida's sort habit). */
function pubmedHref(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname.endsWith("pubmed.ncbi.nlm.nih.gov") && !u.searchParams.has("sort")) {
      u.searchParams.set("sort", "date");
      return u.toString();
    }
  } catch {
    /* not an absolute URL — leave untouched */
  }
  return url;
}
const PUBMED_TIP = "Tip: search the reviewer's FULL name on PubMed — results open sorted by most recent.";

/** Frida-QC R-008: claimed rows stay pinned first in their group for the whole session. */
const CLAIMS_KEY = "qc-claimed-rows";
function loadClaims(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(CLAIMS_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}
function saveClaims(ids: Set<string>): void {
  sessionStorage.setItem(CLAIMS_KEY, JSON.stringify([...ids]));
}

export function QualityCheckPortal(): JSX.Element {
  const can = useCan();
  // July-8 FR-L9: the QC queue is cross-domain for QC-capable users — no domain in the gate,
  // and "All domains" is the default view (domain is an optional filter, not a restriction).
  const [domain, setDomain] = useState<Domain | "">("");
  const allowed = can("qcOpenPortal", {});
  // Students (no QC-portal capability) get their own scoped view instead (FR-J14/L4).
  return allowed ? <QcQueue domain={domain} setDomain={setDomain} /> : <MyQualityChecks />;
}

// ── Full QC queue (AE / QC roles) ─────────────────────────────────────────────
// SoftR R-008 + Frida-QC R-005: the queue opens on the AE's real worklist — fresh Unchecked
// rows, Fixed rows awaiting re-check, and Double-check rows awaiting a senior — as one combined
// default selection.
const DEFAULT_STATUSES = "Unchecked,Fixed,Double Check";
type QueueKind = "in-process" | "complete" | "all";

function QcQueue({ domain, setDomain }: { domain: Domain | ""; setDomain: (d: Domain | "") => void }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const [status, setStatus] = useState<ReviewerStatus | typeof DEFAULT_STATUSES | "">(DEFAULT_STATUSES);
  // Frida-QC R-002 / WO-7/21 Track I: multi-select team filter + the in-process/complete queue
  // split (in-process default). Teams are the geographic unit — there is no separate country
  // field in the data, so the team filter IS the country filter.
  const [teamsSel, setTeamsSel] = useState<string[]>([]);
  const [queue, setQueue] = useState<QueueKind>("in-process");
  const [profileFor, setProfileFor] = useState<{ selectedId: string; title: string } | null>(null);
  // Frida-QC R-007: senior profile edit from the queue (server-gated by qcConfirmDoubleCheck).
  const [editing, setEditing] = useState<QcRow | null>(null);
  // Frida-QC R-008: session-sticky claims — a claimed row leads its group until unclaimed.
  const [claimed, setClaimed] = useState<Set<string>>(loadClaims);
  const toggleClaim = (id: string): void =>
    setClaimed((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveClaims(next);
      return next;
    });

  const params = new URLSearchParams();
  if (domain) params.set("domain", domain);
  if (teamsSel.length) params.set("teams", teamsSel.join(","));
  if (queue !== "all") params.set("queue", queue);
  if (status === DEFAULT_STATUSES) params.set("statuses", DEFAULT_STATUSES);
  else if (status) params.set("status", status);
  // Domain-scoped ability checks: `""` (All domains) must read as "no domain scope".
  const ctx = domain ? { domain } : {};
  const { data, isLoading, error } = useQuery({
    queryKey: qk.qc({ domain, teams: teamsSel.join(","), queue, status }),
    queryFn: () => api.get<QcRow[]>(`/quality-check?${params.toString()}`),
  });

  const setStatusM = useMutation({
    mutationFn: ({ id, status, comments }: { id: string; status: ReviewerStatus; comments?: string }) =>
      api.post(`/quality-check/${id}/status`, { status, comments }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qc"] }),
  });
  const confirmDouble = useMutation({
    mutationFn: (id: string) => api.post(`/quality-check/${id}/confirm-double-check`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qc"] }),
  });

  // Group by preprint, title prominent (FR-J15); each group collapses (FR-K13). Within a group:
  // the AE's claimed rows first (Frida-QC R-008), then Fixed (a fix awaiting re-check) →
  // Double Check (awaiting a senior) → Unchecked → the rest.
  const groups = useMemo(() => {
    const statusRank = (s: ReviewerStatus): number =>
      s === "Fixed" ? 0 : s === "Double Check" ? 1 : s === "Unchecked" ? 2 : 3;
    const rowRank = (p: QcRow): number => (claimed.has(p.id) ? -1 : statusRank(p.status));
    const byPreprint = new Map<
      string,
      { title: string; selectedId: string | null; abstract: string | null; rows: QcRow[] }
    >();
    for (const p of data ?? []) {
      const key = p.preprintId ?? "unknown";
      const g = byPreprint.get(key) ?? {
        title: p.preprintTitle,
        selectedId: p.selectedId,
        abstract: p.preprintAbstract,
        rows: [],
      };
      if (!g.selectedId && p.selectedId) g.selectedId = p.selectedId;
      if (!g.abstract && p.preprintAbstract) g.abstract = p.preprintAbstract;
      g.rows.push(p);
      byPreprint.set(key, g);
    }
    for (const g of byPreprint.values()) {
      g.rows.sort((a, b) => rowRank(a) - rowRank(b));
    }
    return [...byPreprint.entries()];
  }, [data, claimed]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Quality Check</h1>
          <p className="text-sm text-slate-500">Grouped by preprint — review each proposed reviewer and set a status.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Frida-QC R-003/R-004: in-process (default) vs complete queues — complete stays
              reachable after the 40-greenlit cutoff for outstanding student QC + teaching. */}
          <div className="flex overflow-hidden rounded-md border border-slate-200" role="group" aria-label="Queue">
            {(
              [
                ["in-process", "In process"],
                ["complete", "Complete"],
                ["all", "All"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={queue === value}
                className={`px-2.5 py-1.5 text-sm ${queue === value ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setQueue(value)}
              >
                {label}
              </button>
            ))}
          </div>
          {/* FR-L9: domain is an optional filter — the queue itself is cross-domain. */}
          <Select value={domain} onChange={(e) => setDomain(e.target.value as Domain | "")}>
            <option value="">All domains</option>
            {DOMAINS.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </Select>
          {/* Frida-QC R-002 / WO-7/21 Track I: country/team multi-select (fixed RRID vocabulary). */}
          <MultiFilter label="Country / Team" options={RRID_TEAMS} value={teamsSel} onChange={setTeamsSel} />
          <Select value={status} onChange={(e) => setStatus(e.target.value as ReviewerStatus | typeof DEFAULT_STATUSES | "")}>
            <option value={DEFAULT_STATUSES}>Unchecked + Fixed + Double check</option>
            <option value="">All statuses</option>
            {REVIEWER_STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </div>
      </header>

      {isLoading ? (
        <Spinner label="Loading queue…" />
      ) : error ? (
        <EmptyState>Could not load the QC queue.</EmptyState>
      ) : groups.length === 0 ? (
        <EmptyState>No reviewers match this filter.</EmptyState>
      ) : (
        <div className="space-y-4">
          {groups.map(([preprintId, g]) => (
            // FR-K13 + July-8 FR-L7: collapsible group per preprint, COLLAPSED by default — the
            // AE scans a compact list of titles and expands only the one they're working.
            <details key={preprintId} className="rounded-lg border border-slate-200 bg-white shadow-sm">
              <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-t-lg px-4 py-3 hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <PreprintContext
                    selectedId={g.selectedId}
                    title={g.title}
                    abstract={g.abstract}
                    trailing={
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        · {g.rows.length} proposed reviewer{g.rows.length === 1 ? "" : "s"}
                      </span>
                    }
                  />
                </div>
                {g.selectedId && (
                  <Button
                    variant="secondary"
                    onClick={(e) => {
                      e.preventDefault(); // don't toggle the group
                      setProfileFor({ selectedId: g.selectedId!, title: g.title });
                    }}
                  >
                    Reviewer profile
                  </Button>
                )}
              </summary>
              <div className="space-y-2 border-t border-slate-100 p-4">
                {g.rows.map((p) => (
                  <QcProposalRow
                    key={p.id}
                    p={p}
                    onSetStatus={(status, comments) => setStatusM.mutate({ id: p.id, status, comments })}
                    onConfirmDouble={() => confirmDouble.mutate(p.id)}
                    canConfirmDouble={can("qcConfirmDoubleCheck", ctx)}
                    canForStatus={(t) =>
                      can(STATUS_ACTIONS[t]!, ctx) || (t === "Invite" && can("qcJuniorInvite", ctx))
                    }
                    claimed={claimed.has(p.id)}
                    onToggleClaim={() => toggleClaim(p.id)}
                    canEditProfile={can("qcConfirmDoubleCheck", ctx)}
                    onEditProfile={() => setEditing(p)}
                  />
                ))}
              </div>
            </details>
          ))}
        </div>
      )}

      {profileFor && (
        <Modal open onClose={() => setProfileFor(null)} title={`Reviewer profile — ${profileFor.title}`}>
          <ReviewerProfilePanel
            selectedId={profileFor.selectedId}
            canEdit={can("editSimilarArticles", ctx) || can("editPitchDeck", ctx)}
          />
        </Modal>
      )}
      {/* Frida-QC R-007: the senior's profile-edit path (server re-checks the senior gate). */}
      {editing && <EditProfileModal proposal={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function QcProposalRow({
  p,
  onSetStatus,
  onConfirmDouble,
  canConfirmDouble,
  canForStatus,
  claimed,
  onToggleClaim,
  canEditProfile,
  onEditProfile,
}: {
  p: QcRow;
  onSetStatus: (status: ReviewerStatus, comments?: string) => void;
  onConfirmDouble: () => void;
  canConfirmDouble: boolean;
  canForStatus: (t: ReviewerStatus) => boolean;
  claimed: boolean;
  onToggleClaim: () => void;
  canEditProfile: boolean;
  onEditProfile: () => void;
}): JSX.Element {
  const valid = new Set(nextStates("reviewer", p.status));
  const options = SETTABLE_STATUSES.filter((t) => valid.has(t) && canForStatus(t));
  const pubmed = p.pubMedLink ?? p.linkToPublications;
  return (
    <div className={`flex flex-wrap items-start justify-between gap-3 rounded-md border p-2 ${claimed ? "border-brand bg-brand/5" : "border-slate-100"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge tone={statusTone(p.status)}>{p.status}</Badge>
          {p.hIndex != null && <Badge tone="violet">H {p.hIndex}</Badge>}
          {p.team && <Badge tone="slate">{p.team}</Badge>}
          {claimed && <Badge tone="blue">Claimed by you</Badge>}
        </div>
        {/* Frida-QC R-013/R-014: Double-check is the junior→senior assist path — credentials are
            already verified; confirming credits the junior and does NOT take over the preprint. */}
        {p.status === "Double Check" && (
          <div className="mt-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs text-violet-800">
            <span className="font-semibold">Double check — senior assist requested{p.qcTrainingEmail ? ` by ${p.qcTrainingEmail}` : ""}.</span>{" "}
            {canConfirmDouble
              ? "Credentials are already verified — review the expertise + best-match and Confirm → Invite. The requesting QC keeps credit; assisting here doesn't take over their preprint."
              : "Awaiting a senior QC's confirmation."}
          </div>
        )}
        {/* FR-K12: the full reviewer card — the five things + the proposing student. */}
        <h3 className="mt-1 font-medium">
          {p.firstName} {p.lastName}
          {p.reviewerEmail && <span className="ml-2 text-xs font-normal text-slate-500">{p.reviewerEmail}</span>}
        </h3>
        <p className="text-xs text-slate-500">
          {p.subdiscipline ?? "—"} · {p.affiliation ?? "—"}
        </p>
        {/* R-010: same-tab links — SoftR parity is in-place navigation, not new tabs.
            Frida-QC R-011: PubMed opens most-recent-first with a full-name-search nudge. */}
        <div className="mt-0.5 flex flex-wrap gap-3 text-xs">
          {pubmed && (
            <a className="text-brand hover:underline" href={pubmedHref(pubmed)} title={PUBMED_TIP}>
              PubMed
            </a>
          )}
          {p.linkToProfile && (
            <a className="text-brand hover:underline" href={p.linkToProfile}>
              Institutional profile
            </a>
          )}
          {p.doiBestMatch && (
            <a className="text-brand hover:underline" href={bestMatchHref(p.doiBestMatch)}>
              Best-match paper
            </a>
          )}
        </div>
        {p.bestMatchPaperTitle && (
          <p className="mt-1 text-xs text-slate-600">
            <span className="font-medium text-slate-500">Nearest neighbor:</span>{" "}
            {p.doiBestMatch ? (
              <a className="text-brand hover:underline" href={bestMatchHref(p.doiBestMatch)}>
                {p.bestMatchPaperTitle}
              </a>
            ) : (
              p.bestMatchPaperTitle
            )}
          </p>
        )}
        {p.justification && <p className="mt-1 whitespace-pre-wrap text-xs italic text-slate-500">{p.justification}</p>}
        <p className="mt-1 text-xs text-slate-500">
          <span className="font-medium">Proposed by:</span> {p.studentName || "—"}
          {p.proposerEmail && <span className="ml-1">({p.proposerEmail})</span>}
        </p>
        {p.qcComments && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-amber-700">QC (student-facing): {unescapeNewlines(p.qcComments)}</p>
        )}
        {p.qcInternalNote && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-violet-700">Internal note (AE↔AE): {unescapeNewlines(p.qcInternalNote)}</p>
        )}
        {p.studentReply && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-sky-700">Student reply: {unescapeNewlines(p.studentReply)}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Frida-QC R-008: keep this row leading its group while the AE works it. */}
        <Button
          variant="ghost"
          className="py-1"
          onClick={onToggleClaim}
          aria-pressed={claimed}
          title={claimed ? "Release — stop pinning this row first" : "Claim — keep this row first in its group while you work it"}
        >
          {claimed ? "Release" : "📌 Claim"}
        </Button>
        {/* Frida-QC R-007: profile edits from QC are senior-only (server re-checks). */}
        {canEditProfile && (
          <Button variant="secondary" className="py-1" onClick={onEditProfile}>
            Edit profile
          </Button>
        )}
        {p.status === "Double Check" && canConfirmDouble && <Button onClick={onConfirmDouble}>Confirm → Invite</Button>}
        {options.length > 0 && (
          <Select
            value=""
            aria-label="Set status"
            onChange={(e) => {
              const t = e.target.value as ReviewerStatus;
              if (!t) return;
              // FR-K14: Fixable + Reject collect student-facing feedback; Double Check collects
              // the senior-AE-facing note (all required). Frida-QC R-012: Invite takes an
              // OPTIONAL positive note — cancel/empty just proceeds.
              const prompt = COMMENT_PROMPTS[t];
              let comments: string | undefined;
              if (prompt) {
                const entered = window.prompt(prompt);
                if (OPTIONAL_COMMENT_STATUSES.has(t)) {
                  comments = entered?.trim() ? entered : undefined;
                } else {
                  if (!entered?.trim()) return;
                  comments = entered;
                }
              }
              onSetStatus(t, comments);
            }}
          >
            <option value="">Set status…</option>
            {options.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        )}
      </div>
    </div>
  );
}

// ── Student "My Quality Checks" (FR-J14, July-7 FR-K16) ──────────────────────
// Home's Needs-Attention CTA lands here with ?attention=1 — Fixable rows lead and scroll
// into view; the student opens Edit themselves.
function MyQualityChecks(): JSX.Element {
  const [searchParams] = useSearchParams();
  const focusAttention = searchParams.get("attention") === "1";
  const [editing, setEditing] = useState<QcRow | null>(null);
  const scrolledAttention = useRef(false);
  const { data, isLoading, error } = useQuery({
    queryKey: qk.qc({ mine: "1" }),
    queryFn: () => api.get<QcRow[]>(`/quality-check/mine`),
  });

  const rows = data ?? [];
  const needsFix = useMemo(() => rows.filter((p) => p.status === "Fixable"), [rows]);
  const otherFeedback = useMemo(() => rows.filter((p) => p.status !== "Fixable"), [rows]);
  // WO-7/21 Track C: fixables group by preprint (like the AE queue) so five fixes across five
  // preprints each carry their context.
  const fixGroups = useMemo(() => {
    const byPreprint = new Map<string, { title: string; selectedId: string | null; abstract: string | null; rows: QcRow[] }>();
    for (const p of needsFix) {
      const key = p.preprintId ?? "unknown";
      const g = byPreprint.get(key) ?? {
        title: p.preprintTitle,
        selectedId: p.selectedId,
        abstract: p.preprintAbstract,
        rows: [],
      };
      if (!g.selectedId && p.selectedId) g.selectedId = p.selectedId;
      if (!g.abstract && p.preprintAbstract) g.abstract = p.preprintAbstract;
      g.rows.push(p);
      byPreprint.set(key, g);
    }
    return [...byPreprint.entries()];
  }, [needsFix]);

  // Deep-link from Home: scroll to the Fixable section (do not auto-open Edit).
  useEffect(() => {
    if (!focusAttention || scrolledAttention.current || needsFix.length === 0) return;
    scrolledAttention.current = true;
    document.getElementById(`qc-row-${needsFix[0]!.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusAttention, needsFix]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-bold">My Quality Checks</h1>
        <p className="text-sm text-slate-500">
          Your proposals the quality checkers have acted on (still-unchecked ones stay in Find Reviewers). When a
          reviewer is marked Fixable, open Edit to update the profile and mark it Fixed.
        </p>
      </header>
      {isLoading ? (
        <Spinner label="Loading your proposals…" />
      ) : error ? (
        <EmptyState>Could not load your proposals.</EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState>You haven’t proposed any reviewers yet.</EmptyState>
      ) : (
        <div className="space-y-6">
          {needsFix.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-800">
                Needs your fix ({needsFix.length})
              </h2>
              {/* WO-7/21 Track C: grouped by preprint, each fix with its reviewer context. */}
              {fixGroups.map(([preprintId, g]) => (
                <div key={preprintId} className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
                  <PreprintContext
                    selectedId={g.selectedId}
                    title={g.title}
                    abstract={g.abstract}
                    trailing={
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {g.rows.length} fix{g.rows.length === 1 ? "" : "es"}
                      </span>
                    }
                  />
                  {g.rows.map((p) => (
                    <StudentQcCard
                      key={p.id}
                      proposal={p}
                      highlight={focusAttention && p.id === needsFix[0]?.id}
                      onEdit={() => setEditing(p)}
                    />
                  ))}
                </div>
              ))}
            </section>
          )}
          {otherFeedback.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Other feedback ({otherFeedback.length})
              </h2>
              <div className="space-y-2">
                {otherFeedback.map((p) => (
                  <StudentQcCard key={p.id} proposal={p} onEdit={() => setEditing(p)} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
      {editing && <EditProfileModal proposal={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function StudentQcCard({
  proposal: p,
  highlight = false,
  onEdit,
}: {
  proposal: QcRow;
  highlight?: boolean;
  onEdit: () => void;
}): JSX.Element {
  const pubmed = p.pubMedLink ?? p.linkToPublications;
  return (
    <div id={`qc-row-${p.id}`}>
      <Card className={highlight ? "ring-2 ring-amber-400" : undefined}>
        <CardBody className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Badge tone={statusTone(p.status)}>{p.status}</Badge>
              {p.domain && <Badge tone="blue">{p.domain}</Badge>}
            </div>
            <h3 className="mt-1 font-medium">
              {p.firstName} {p.lastName}
            </h3>
            {p.status !== "Fixable" && (
              <div className="mt-1">
                <PreprintContext
                  selectedId={p.selectedId}
                  title={p.preprintTitle}
                  abstract={p.preprintAbstract}
                  compact
                />
              </div>
            )}
            {/* WO-7/21 Track C: the reviewer-profile context needed to act on the AE's note —
                sub-discipline, affiliation, best-match — not just "confirm it matches". */}
            <p className="text-xs text-slate-500">
              {[p.subdiscipline && `sub-discipline: ${p.subdiscipline}`, p.affiliation].filter(Boolean).join(" · ") || "—"}
            </p>
            {p.bestMatchPaperTitle && (
              <p className="text-xs text-slate-500">
                Best match:{" "}
                {p.doiBestMatch ? (
                  <a className="text-brand hover:underline" href={bestMatchHref(p.doiBestMatch)}>
                    {p.bestMatchPaperTitle}
                  </a>
                ) : (
                  p.bestMatchPaperTitle
                )}
              </p>
            )}
            {(pubmed || p.linkToProfile) && (
              <p className="flex gap-3 text-xs">
                {p.linkToProfile && (
                  <a className="text-brand hover:underline" href={p.linkToProfile}>
                    Institutional profile
                  </a>
                )}
                {pubmed && (
                  <a className="text-brand hover:underline" href={pubmedHref(pubmed)} title={PUBMED_TIP}>
                    PubMed
                  </a>
                )}
              </p>
            )}
            {p.qcComments && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-amber-700">
                AE note: {unescapeNewlines(p.qcComments)}
              </p>
            )}
            {p.studentReply && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-sky-700">Your reply: {unescapeNewlines(p.studentReply)}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Edit opens the profile modal; Mark as Fixed is only inside that modal. */}
            {p.status === "Fixable" && (
              <Button variant="secondary" onClick={onEdit}>
                Edit
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/** Edit the reviewer profile to apply a QC fix (FR-K16) — PATCH /quality-check/:id/profile,
 *  optional reply, then mark Fixed in one action. */
function EditProfileModal({ proposal, onClose }: { proposal: QcRow; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState({
    affiliation: proposal.affiliation ?? "",
    secondaryAffiliation: proposal.secondaryAffiliation ?? "",
    highestDegree: proposal.highestDegree ?? "",
    otherDegree: proposal.otherDegree ?? "",
    title: proposal.title ?? "",
    otherTitles: proposal.otherTitles ?? "",
    subdiscipline: proposal.subdiscipline ?? "",
    linkToProfile: proposal.linkToProfile ?? "",
    publicationLink: proposal.linkToPublications ?? proposal.pubMedLink ?? "",
    bestMatchPaperTitle: proposal.bestMatchPaperTitle ?? "",
  });
  const [reply, setReply] = useState("");
  const saveAndFix = useMutation({
    mutationFn: async () => {
      await api.patch(`/quality-check/${proposal.id}/profile`, f);
      if (reply.trim()) await api.post(`/quality-check/${proposal.id}/reply`, { comment: reply.trim() });
      await api.post(`/quality-check/${proposal.id}/fixed`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qc"] });
      onClose();
    },
  });
  const set = (key: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [key]: e.target.value });
  const incomplete =
    !f.secondaryAffiliation.trim() ||
    !f.highestDegree.trim() ||
    !f.title.trim() ||
    !f.linkToProfile.trim() ||
    !f.publicationLink.trim();
  return (
    <Modal open onClose={onClose} title={`Edit reviewer profile — ${proposal.firstName} ${proposal.lastName}`}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">On “{proposal.preprintTitle}”</p>
        {proposal.qcComments && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
            <span className="font-semibold">The AE asked:</span> {unescapeNewlines(proposal.qcComments)}
          </div>
        )}
        <Field label="Affiliation">
          <Input value={f.affiliation} onChange={set("affiliation")} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Secondary affiliation (required)">
            <Input value={f.secondaryAffiliation} onChange={set("secondaryAffiliation")} required />
          </Field>
          <Field label="Highest degree (required)">
            <Input value={f.highestDegree} onChange={set("highestDegree")} required />
          </Field>
        </div>
        <Field label="Other Degrees">
          <Input value={f.otherDegree} onChange={set("otherDegree")} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Title (required)">
            <Input value={f.title} onChange={set("title")} required />
          </Field>
          <Field label="Other titles">
            <Input value={f.otherTitles} onChange={set("otherTitles")} />
          </Field>
        </div>
        <Field label="Institutional profile (link) (required)">
          <Input value={f.linkToProfile} onChange={set("linkToProfile")} required />
        </Field>
        <Field label="Publication / PubMed link (required)">
          <Input
            value={f.publicationLink}
            onChange={set("publicationLink")}
            placeholder="https://pubmed.ncbi.nlm.nih.gov/…"
            required
          />
        </Field>
        <Field label="Because of your expertise in…">
          <Input
            value={f.subdiscipline}
            onChange={set("subdiscipline")}
            placeholder="e.g. computational epidemiology"
          />
        </Field>
        <Field label="Best-match paper title">
          <Input value={f.bestMatchPaperTitle} onChange={set("bestMatchPaperTitle")} />
        </Field>
        <div className="rounded-md border border-blue-100 bg-blue-50 p-2.5 text-sm text-blue-950">
          <span className="text-xs font-semibold text-blue-900">Justification preview</span>
          <p className="mt-1 text-xs text-blue-800/80">
            This sentence is copied into the reviewer invitation email.
          </p>
          <p className="mt-1.5 italic">
            Because of your expertise in {f.subdiscipline.trim() || "…"}, and especially your paper "
            {f.bestMatchPaperTitle.trim() || "…"}", we think you'd be a terrific reviewer for this preprint.
          </p>
        </div>
        <Field label="Reply to the checker">
          <Input
            placeholder="e.g. “I’ve made the correction”"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => saveAndFix.mutate()} disabled={incomplete || saveAndFix.isPending}>
            {saveAndFix.isPending ? "Marking…" : "Mark as Fixed"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
